/* snapping.js — cabinet snapping (nearest wall, inner-span corners, neighbor magnet).
 * Ported from lhc-app.html: snapToNearestWall (~32143), lhcWallInnerSpan (~31388),
 * snapSides (~32376). Internal units = METRES.
 *
 * Pure: functions take (walls, lib, …) explicitly and RETURN values (no mutation of the
 * passed placement — callers assign the returned absLeft), unlike the original which
 * mutated p.absLeft in place.
 */
import { SNAP_WALL_DIST, SNAP_SIDE_MM, SNAP_SIDE_LEADING_M } from "./constants";
import { pairMinGapLR, golaChannelsFor } from "./golaClearance";
import { panelSpanOf, sidePanelExtents } from "./sidePanelClearance";
import { getWallPlacementInfo, lhcRoomNormal } from "./placement";
import { layerCollides } from "./collision";
import { jointsCoincide, getWallGraph } from "./wallGraph";
import { effectiveThickness, MITER_LIMIT_RATIO } from "./wallJoins";

/* [FLIP][W4 roomEdit — wallInnerSpan CONTINUATION FIX] wallInnerSpan is angle-blind:
 * a COLLINEAR neighbour (shared node kind === 'continuation', e.g. the joint created
 * by roomEdit.insertVertex) injects a spurious effectiveThickness/2 inset on BOTH
 * halves even though no corner exists there. The principled fix — skip the inset for
 * neighbours whose shared node is a 'continuation' per getWallGraph — shipped
 * flip-commented; FLIPPED ON 2026-07-17 (user mandate BUG 1: "the cabinet must reach
 * the true inside corner — the legal placement origin is the inside wall face, never
 * the centerline node"). A collinear junction has NO physical face on the run, so it
 * contributes NO inset: cabinets slide flush across/against continuation joints.
 * insertVertex's effectiveThickness/2 refusal band around split points remains as an
 * independent guard (roomEdit.js splitRemapRuling). */
export const SKIP_CONTINUATION_INSET = true;

/** lhcWallInnerSpan(wall, walls): legal absLeft span shrunk by the NEIGHBOUR's
 *  half-thickness at connected corners. Returns {off0, off1, len, innerLen}. (~31388)
 *  Stage W1 (per-wall thickness): the inset at each end is the max of
 *  effectiveThickness(neighbour)/2 over the joined neighbours — identity with the
 *  legacy WALL_THICKNESS/2 whenever no wall carries a thickness override.
 *  2026-09-28: a thin view over wallFaceSpan (THE corner-span oracle below). */
export function wallInnerSpan(wall, walls) {
  return wallInnerSpanEx(wall, walls, { skipContinuation: SKIP_CONTINUATION_INSET });
}

/** THE corner-neighbour policy (single source — span math, corner objects and any
 *  future consumer must share it, never re-derive it):
 *  given the host wall's interior normal and a neighbour w2 meeting at `node`,
 *  returns the SIGNED span offset contributed by w2:
 *    +t/2 → CONCAVE  (w2's body on the cabinet side; its inner face is the stop)
 *    −t/2 → CONVEX   (w2 steps away; the band extends to the outer mitre corner)
 *  |dot| < 0.05 (near-collinear continuations) falls to +t/2 — legacy parity. */
export function cornerNeighborOffset(interiorNormal, w2, node) {
  const other = jointsCoincide(w2.start, node) ? w2.end : w2.start;
  const dx = other[0] - node[0], dz = other[1] - node[1];
  const L2 = Math.hypot(dx, dz) || 1;
  const t = effectiveThickness(w2) / 2;
  return ((dx / L2) * interiorNormal[0] + (dz / L2) * interiorNormal[1]) < -0.05 ? -t : t;
}

/** Explicit-options form of wallInnerSpan (the flip-fix testbed): with
 *  { skipContinuation: true } a neighbour whose shared node is a 'continuation'
 *  (collinear joint) contributes NO inset. Default path is byte-identical to the
 *  legacy wallInnerSpan at PERPENDICULAR (90°) corners.
 *  Returns the plain {off0, off1, len, innerLen} view of wallFaceSpan. */
export function wallInnerSpanEx(wall, walls, opts = {}) {
  const fs = wallFaceSpan(wall, walls, { skipContinuation: !!opts.skipContinuation });
  return { off0: fs.off0, off1: fs.off1, len: fs.len, innerLen: fs.innerLen };
}

/* ═══ wallFaceSpan — THE corner-span oracle (2026-09-28 P0 regression fix) ═══
 *
 * THE BUG (user: "the cabinets stop before the corner / go outside the wall"):
 * since the 2026-09-15 immutable-authoring contract consecutive legs share a
 * graph NODE but their centerline endpoints are up to one thickness apart
 * (each leg is its own reference offset by t/2; at a 90° corner the two
 * endpoints sit 2h·sin45° = 106 mm apart). The span math kept measuring the
 * corner from the HOST's own endpoint as if the neighbour's endpoint were the
 * same point, so every authored corner came out exactly one half-thickness
 * wrong: concave corners reserved +75 mm of wall that is not there (the run
 * stopped 75 mm short of the corner), convex corners extended −75 mm past the
 * true face corner (the unit rendered inside the neighbour wall / past the
 * corner). Every consumer (clamp, magnets, validation, elevation, dims,
 * fillers, rules, Magic Fix) inherited the error because they all read
 * wallInnerSpan — which is also why ONE fix here fixes them all.
 *
 * THE CONTRACT. The span end of a wall on the cabinet side is the point where
 * the wall's INTERIOR FACE physically ends — the join's interior-face mitre
 * vertex (concave / inside corner) or the outer mitre corner (convex / outside
 * corner) — i.e. the intersection of the host's interior face line with the
 * neighbour's face line, each line anchored on ITS OWN wall's centerline
 * endpoint (the own-axis rule the join solver renders with, wallJoins.js
 * ANCHOR). Node membership (which wall-ends meet here) comes from the wall
 * graph — never from raw endpoint proximity — so a leg the graph captured by
 * its face vertex is a corner neighbour here too. off = signed distance from
 * the host's own endpoint to that vertex along the host (positive = the span
 * shrinks, negative = the span extends past the endpoint to the visible
 * corner).
 *
 * BYTE-IDENTICAL LEGACY: when the neighbour's endpoint coincides with the
 * host's (every pre-2026-09-15 room, every welded joint, every existing pin)
 * the computation is the untouched legacy path — cornerMiterOffset with its
 * flat ±t2/2 at (near-)90° / convex / collinear / arc branches and the exact
 * mitre at oblique concave corners. Only a NON-coincident neighbour endpoint
 * takes the own-axis intersection (identical to the legacy value at exactly
 * 90° by construction; at oblique corners it is the join solver's vertex, so a
 * leg drawn INTO a corner reaches exactly the drawn reference vertex).
 * Peninsula neighbours (guide lines, no face) are ignored; a continuation
 * node contributes nothing (SKIP_CONTINUATION_INSET); per-wall thickness via
 * effectiveThickness. Cost: one memoized graph lookup per call. */
export function wallFaceSpan(wall, walls, opts = {}) {
  // engine policy by default (SKIP_CONTINUATION_INSET); wallInnerSpanEx passes it explicitly
  const skipContinuation = opts.skipContinuation === undefined ? SKIP_CONTINUATION_INSET : !!opts.skipContinuation;
  const info = getWallPlacementInfo(wall, walls);
  const len = info ? info.len : 0;
  const rnSpan = wall ? lhcRoomNormal(wall, walls) : null;
  const nx = rnSpan ? rnSpan.nx : (info ? info.nx : 0);
  const nz = rnSpan ? rnSpan.nz : (info ? info.nz : 0);
  const t1 = wall ? effectiveThickness(wall) / 2 : 0;
  const hStart = info ? [info.dirX, info.dirZ] : null;          // start → end
  const hEnd = info ? [-info.dirX, -info.dirZ] : null;          // end   → start
  const ends = [
    _faceSpanEnd(wall, walls, "start", wall ? wall.start : null, hStart, [nx, nz], t1, skipContinuation),
    _faceSpanEnd(wall, walls, "end", wall ? wall.end : null, hEnd, [nx, nz], t1, skipContinuation),
  ];
  const off0 = ends[0].off, off1 = ends[1].off;
  return {
    off0, off1, len, innerLen: Math.max(0, len - off0 - off1),
    interiorNormal: [nx, nz],
    ends,
    node0: ends[0].node, node1: ends[1].node,
    vertex0: ends[0].vertex, vertex1: ends[1].vertex,
    kind0: ends[0].kind, kind1: ends[1].kind,
  };
}

/** One end of wallFaceSpan: node membership + the per-neighbour face offsets.
 *  → { end, node, kind, off, vertex, neighbors:[{wallId, end, off, kind}] }
 *  kind: 'free' | 'concave' | 'convex' | 'continuation' | 'peninsula' */
function _faceSpanEnd(wall, walls, endName, ownPt, hostOut, interiorNormal, hostHalfT, skipContinuation) {
  const out = { end: endName, node: null, kind: "free", off: 0, vertex: null, neighbors: [] };
  if (!wall || !ownPt) return out;
  const members = _cornerNeighborsAt(wall, walls, endName, ownPt);
  out.node = members.nodeId;
  if (members.continuation && skipContinuation) {
    out.kind = "continuation";
    return out;
  }
  let best = null;
  members.list.forEach(({ w2, w2End }) => {
    if ((w2.style || "standard") === "peninsula") { out.neighbors.push({ wallId: w2.id, end: w2End, off: 0, kind: "peninsula" }); return; }
    const off = cornerFaceOffset(interiorNormal, w2, w2End, ownPt, hostOut, hostHalfT);
    // kind = THE corner policy's sign (the neighbour's own chord vs the host's interior
    // side), never the sign of `off` — an authored corner's offset is 0 ± 1e-16.
    const kind = members.continuation ? "continuation" : (_legacyCornerOffset(interiorNormal, w2, w2End) < 0 ? "convex" : "concave");
    out.neighbors.push({ wallId: w2.id, end: w2End, off, kind });
    if (best === null || off > best.off) best = { off, kind };
  });
  if (!best) return out;
  out.off = best.off;
  out.kind = best.kind;
  if (hostOut) {
    out.vertex = [
      ownPt[0] + hostOut[0] * best.off + interiorNormal[0] * hostHalfT,
      ownPt[1] + hostOut[1] * best.off + interiorNormal[1] * hostHalfT,
    ];
  }
  return out;
}

/** The wall-ENDS meeting the host at `endName`, from the wall graph's node
 *  membership (the canonical topology — includes face-vertex captures the raw
 *  distance test misses). Falls back to the raw jointsCoincide pairing only
 *  for a host the graph does not carry (degenerate). One entry per neighbour
 *  wall; when a tiny neighbour has BOTH ends in the node the start end wins
 *  (legacy cornerMiterOffset order). Never the host's own other end. */
function _cornerNeighborsAt(wall, walls, endName, ownPt) {
  const graph = getWallGraph(walls);
  const wi = graph.walls.get(wall.id);
  const byId = new Map();
  (walls || []).forEach((w) => { if (w && w.id !== wall.id) byId.set(w.id, w); });
  const list = [];
  const seen = new Map(); // wallId → index in list
  const push = (w2, w2End) => {
    if (!w2) return;
    const k = seen.get(w2.id);
    if (k == null) { seen.set(w2.id, list.length); list.push({ w2, w2End }); }
    else if (w2End === "start" && list[k].w2End !== "start") list[k].w2End = "start";
  };
  if (wi) {
    const nodeId = endName === "start" ? wi.startNode : wi.endNode;
    const node = (graph.nodes || []).find((n) => n.id === nodeId) || null;
    const nbs = endName === "start" ? wi.neighborsAtStart : wi.neighborsAtEnd;
    (nbs || []).forEach((nb) => push(byId.get(nb.wallId), nb.end));
    return { nodeId, continuation: !!(node && node.kind === "continuation") || _isCollinearPair(wall, endName, list), list };
  }
  // degenerate host (not in the graph): legacy raw pairing
  const near = (a, b) => jointsCoincide(a, b);
  byId.forEach((w2) => {
    if (!Array.isArray(w2.start) || !Array.isArray(w2.end)) return;
    if (near(w2.start, ownPt)) push(w2, "start");
    else if (near(w2.end, ownPt)) push(w2, "end");
  });
  return { nodeId: null, continuation: false, list };
}

/** The graph's continuation rule (≤ COLLINEAR_DEG off straight, wallGraph.js §4)
 *  applied to the two walls' OWN axes. The graph classifies a degree-2 node from
 *  its half-edge directions between node CENTROIDS; in a leg-per-commit room the
 *  corner centroids sit t/4 off the wall axes, so a wall split at a collinear
 *  joint reads ~2.1° "corner" there and would inherit the legacy +t/2 continuation
 *  inset on both halves (the dead band SKIP_CONTINUATION_INSET removed). For
 *  coincident geometry the centroids ARE the endpoints and both rules agree. */
const SPAN_COLLINEAR_DEG = 2;
function _isCollinearPair(wall, endName, list) {
  if (list.length !== 1 || wall.type === "arc") return false;
  const { w2, w2End } = list[0];
  if (w2.type === "arc" || !Array.isArray(w2.start) || !Array.isArray(w2.end)) return false;
  const own = endName === "start" ? wall.start : wall.end, far = endName === "start" ? wall.end : wall.start;
  const own2 = w2End === "start" ? w2.start : w2.end, far2 = w2End === "start" ? w2.end : w2.start;
  const a = [far[0] - own[0], far[1] - own[1]], b = [far2[0] - own2[0], far2[1] - own2[1]];
  const La = Math.hypot(a[0], a[1]), Lb = Math.hypot(b[0], b[1]);
  if (La < 1e-9 || Lb < 1e-9) return false;
  const ang = Math.acos(Math.max(-1, Math.min(1, (a[0] * b[0] + a[1] * b[1]) / (La * Lb)))) * 180 / Math.PI;
  return Math.abs(180 - ang) <= SPAN_COLLINEAR_DEG;
}

/** cornerFaceOffset(interiorNormal, w2, w2End, hostEnd, hostOut, hostHalfT): the
 *  SIGNED along-host offset from the host's OWN endpoint `hostEnd` to the point
 *  where the host's interior face meets neighbour w2's face — w2's face lines
 *  anchored on w2's OWN endpoint (`w2End`). Coincident endpoints (F === E) take
 *  the untouched legacy cornerMiterOffset path (byte-identical); otherwise the
 *  own-axis intersection with the neighbour's ROOM-SIDE face (the fan's adjacent
 *  face, σ = ∓1 by the host's interior side):
 *    s = (σ·t2 − h1·(n·p2) + (F−E)·p2) / (hostOut·p2),  p2 = leftPerp(u2)
 *  concave (w2's body on the cabinet side) → the deeper crossing (physical stop);
 *  convex  (w2 steps away)                 → the outer mitre corner,
 *  bounded by the join solver's miter-limit discipline (a spike past
 *  MITER_LIMIT_RATIO·max(h) bevels ⇒ the face ends at the host's own corner).
 *  Arc / collinear neighbours keep the flat legacy value shifted by the
 *  along-host component of the endpoint gap. */
export function cornerFaceOffset(interiorNormal, w2, w2End, hostEnd, hostOut, hostHalfT) {
  const F = w2End === "start" ? w2.start : w2.end;
  const dX = F[0] - hostEnd[0], dZ = F[1] - hostEnd[1];
  if (dX === 0 && dZ === 0) return cornerMiterOffset(interiorNormal, w2, F, hostOut, hostHalfT);
  const legacy = _legacyCornerOffset(interiorNormal, w2, w2End);
  if (!hostOut) return legacy;
  const alongShift = dX * hostOut[0] + dZ * hostOut[1];
  if (w2.type === "arc") return legacy + alongShift;
  const other = w2End === "start" ? w2.end : w2.start;
  let u2x = other[0] - F[0], u2z = other[1] - F[1];
  const L2 = Math.hypot(u2x, u2z) || 1;
  u2x /= L2; u2z /= L2;
  const p2x = -u2z, p2z = u2x;
  const hDotP2 = hostOut[0] * p2x + hostOut[1] * p2z;
  if (Math.abs(hDotP2) < 1e-6) return legacy + alongShift; // (near-)collinear continuation
  const t2 = effectiveThickness(w2) / 2;
  const nDotP2 = interiorNormal[0] * p2x + interiorNormal[1] * p2z;
  const dDotP2 = dX * p2x + dZ * p2z;
  // THE FAN'S ADJACENCY (wallJoins §fan): the host's interior-side wedge pairs the
  // host's interior face with the neighbour's face on the SAME room side — its
  // dirOut-RIGHT face when the host's interior is dirOut-LEFT, and vice versa
  // (the two dirOut vectors run opposite to the chain's travel at exactly one of
  // the two ends). That face is the neighbour's room-side face: the deeper
  // crossing at a concave corner, the outer mitre corner at a convex one — the
  // legacy max/min choice, without a sign threshold.
  const hostInteriorLeft = (-hostOut[1]) * interiorNormal[0] + hostOut[0] * interiorNormal[1] > 0;
  const sigma = hostInteriorLeft ? -1 : 1;
  const s = (sigma * t2 - hostHalfT * nDotP2 + dDotP2) / hDotP2;
  if (legacy > 0) return s;                                 // concave: the physical stop, any angle (no limit — legacy)
  const limit = MITER_LIMIT_RATIO * Math.max(hostHalfT, t2);
  return Math.hypot(s, hostHalfT) > limit + 1e-9 ? 0 : s;   // convex: beveled spike ⇒ own square corner
}

/** Legacy concave/convex sign from the neighbour's OWN chord (its endpoint at the
 *  node → its far endpoint) — identical to cornerNeighborOffset when the endpoints
 *  coincide, and not tilted by the endpoint gap when they don't. */
function _legacyCornerOffset(interiorNormal, w2, w2End) {
  const from = w2End === "start" ? w2.start : w2.end;
  const other = w2End === "start" ? w2.end : w2.start;
  const dx = other[0] - from[0], dz = other[1] - from[1];
  const L2 = Math.hypot(dx, dz) || 1;
  const t = effectiveThickness(w2) / 2;
  return ((dx / L2) * interiorNormal[0] + (dz / L2) * interiorNormal[1]) < -0.05 ? -t : t;
}

/** cornerMiterOffset(interiorNormal, w2, node, hostOut, hostHalfT): the SIGNED
 *  along-host span offset contributed by neighbour w2 at `node`, measured along
 *  hostOut (node → host body). Superset of cornerNeighborOffset:
 *   - CONVEX (cornerNeighborOffset < 0)         → −t2/2 unchanged (extends the span).
 *   - COLLINEAR / (near-)parallel to the host   → +t2/2 unchanged (continuation inset).
 *   - CONCAVE at ANY angle                       → the EXACT along-host distance from
 *     the node to where the host interior face (offset hostHalfT in interiorNormal)
 *     crosses the neighbour's near interior face (offset ±t2/2 from its centerline).
 *     = +t2/2 at 90°; smaller at obtuse; larger at acute. This is the fix.
 *  hostOut may be null (degenerate host) → falls back to the flat legacy value.
 *  COINCIDENT-ENDPOINT path only (the node IS the host's endpoint) — wallFaceSpan
 *  routes non-coincident neighbours through cornerFaceOffset. */
export const PERP_SNAP_SIN = 0.06; // |sin(host,neighbour)| below this (~3.4° off 90°)
                                   // snaps to the flat +t2/2 — cm-scale endpoint
                                   // irregularity at a meant-to-be-square corner must
                                   // not perturb the inset (v0.50/v0.52 parity).
export function cornerMiterOffset(interiorNormal, w2, node, hostOut, hostHalfT) {
  const legacy = cornerNeighborOffset(interiorNormal, w2, node);
  if (legacy <= 0 || !hostOut) return legacy; // convex / open / degenerate: unchanged
  // Neighbour's OWN outward segment direction from the shared endpoint (its face is
  // parallel to its centerline — NOT the node→far-endpoint chord, which a cm-scale
  // corner gap would tilt). Arc neighbours keep the flat legacy value.
  if (w2.type === "arc") return legacy;
  const nearStart = jointsCoincide(w2.start, node);
  const from = nearStart ? w2.start : w2.end;
  const other = nearStart ? w2.end : w2.start;
  let u2x = other[0] - from[0], u2z = other[1] - from[1];
  const L2 = Math.hypot(u2x, u2z) || 1;
  u2x /= L2; u2z /= L2;
  // Near-perpendicular ⇒ the flat inset is exact; skip the mitre (keeps the pinned
  // ±t2/2 at irregular square corners, applies the mitre only to real angled walls).
  if (Math.abs(hostOut[0] * u2x + hostOut[1] * u2z) < PERP_SNAP_SIN) return legacy;
  const t2 = effectiveThickness(w2) / 2;
  // p2 = leftPerp(neighbour dir); host interior face crosses the neighbour face lines
  // (perp offset ±t2) at s = (±t2 − hostHalfT·(n·p2)) / (hostOut·p2). Concave stop =
  // the deeper crossing (max). hostOut·p2 → 0 ⇒ host ∥ neighbour (collinear) ⇒ legacy.
  const p2x = -u2z, p2z = u2x;
  const hDotP2 = hostOut[0] * p2x + hostOut[1] * p2z;
  if (Math.abs(hDotP2) < 1e-6) return legacy; // (near-)collinear continuation
  const nDotP2 = interiorNormal[0] * p2x + interiorNormal[1] * p2z;
  const sPlus = (t2 - hostHalfT * nDotP2) / hDotP2;
  const sMinus = (-t2 - hostHalfT * nDotP2) / hDotP2;
  return Math.max(sPlus, sMinus);
}

/** clampAbsLeftToSpan(absLeft, width, wall, walls): THE one legal-position clamp —
 *  [off0, len − off1 − width] with SIGNED corner offsets (see wallInnerSpanEx).
 *  Every mover (drag, keyboard nudge, From-corner field, contact clamp, pipeline)
 *  must use this instead of a hand-rolled Math.max(0, …): a literal 0 floor pins
 *  the cabinet to the CENTERLINE NODE and forbids the flush convex corner. */
export function clampAbsLeftToSpan(absLeft, width, wall, walls) {
  const sp = wallInnerSpan(wall, walls);
  const lo = sp.off0;
  const hi = Math.max(lo, sp.len - sp.off1 - (width || 0));
  return Math.max(lo, Math.min(absLeft, hi));
}

/** snapToNearestWall(wx, wz, cabWidth, walls, snapDist[, preferWallId]): nearest wall +
 *  clamped absLeft, or null. Centers the cabinet (B5) when the inner span is shorter than
 *  the cabinet. (~32143)
 *  preferWallId (Phase-1 OWNERSHIP HYSTERESIS): near a corner two walls are almost
 *  equidistant and the owner used to flip per pixel mid-drag. The cabinet's CURRENT wall
 *  keeps ownership unless another wall is decisively closer (by > 75mm) — deterministic,
 *  identical result for identical inputs. */
export function snapToNearestWall(wx, wz, cabWidth, walls, snapDist = SNAP_WALL_DIST, preferWallId = null) {
  const OWNERSHIP_BIAS = 0.075; // metres another wall must win by to steal the cabinet
  let best = null, bestDist = Infinity, preferred = null;
  walls.forEach((w) => {
    const info = getWallPlacementInfo(w, walls);
    if (!info) return;
    const toX = wx - w.start[0], toZ = wz - w.start[1];
    const along = toX * info.dirX + toZ * info.dirZ;
    const alongCl = Math.max(0, Math.min(along, info.len));
    const px = w.start[0] + info.dirX * alongCl;
    const pz = w.start[1] + info.dirZ * alongCl;
    const d = Math.hypot(wx - px, wz - pz);
    if (d > snapDist) return;
    const _sp = wallInnerSpan(w, walls);
    let _clampLo = _sp.off0;
    let _clampHi = info.len - _sp.off1 - cabWidth;
    if (_clampHi < _clampLo) {
      const _c = _sp.off0 + (_sp.innerLen - cabWidth) / 2;
      _clampLo = _c; _clampHi = _c;
    }
    const absLeft = Math.max(_clampLo, Math.min(alongCl - cabWidth / 2, _clampHi));
    const cand = { wall: w, absLeft, dist: d };
    if (preferWallId && w.id === preferWallId) preferred = cand;
    if (d < bestDist) { bestDist = d; best = cand; }
  });
  if (preferred && best && best.wall.id !== preferred.wall.id
      && best.dist > preferred.dist - OWNERSHIP_BIAS) return preferred;
  return best;
}

/** snapSides(p, placements, lib, walls, moveDir): neighbor/wall-end magnet; returns the
 *  new absLeft for p (does NOT mutate p). (~32376)
 *  moveDir: -1 toward wall start, +1 toward end (directional leading-side reach). */
export function snapSides(p, placements, lib, walls, moveDir, snapDistMm = SNAP_SIDE_MM) {
  const def = lib[p.type];
  if (!def) return p.absLeft;
  const wall = walls.find((w) => w.id === p.wallId);
  if (!wall) return p.absLeft;
  const info = getWallPlacementInfo(wall, walls);
  if (!info) return p.absLeft;
  const wallLen = info.len;
  const snapDistM = snapDistMm / 1000;
  const snapLeftM = moveDir === -1 ? SNAP_SIDE_LEADING_M : snapDistM;
  const snapRightM = moveDir === 1 ? SNAP_SIDE_LEADING_M : snapDistM;

  // FACE FILTER (2026-07-14 peninsula fix): on a double-sided wall only
  // SAME-FACE units are neighbours — the other face's run must neither block
  // nor magnet (they don't touch in the real world).
  const _dblSided = (wall.style === "partition" || wall.style === "peninsula");
  const _fsOf = (q) => (q.faceSign === -1 ? -1 : 1);
  const _sameFace = (q) => !_dblSided || _fsOf(q) === _fsOf(p);
  const neighbors = placements
    .filter((q) => {
      if (q === p || q.id === p.id || (q.wallId || null) !== (p.wallId || null)) return false;
      if (!_sameFace(q)) return false;
      const qd = lib[q.type];
      return qd ? layerCollides(def.role, qd.role) : true;
    })
    .sort((a, b) => a.absLeft - b.absLeft);
  // STACKING ALIGNMENT (2026-07-14 CAD mandate, Issue 8): same-wall units in a
  // DIFFERENT vertical band (a wall unit above a base run) contribute
  // EDGE-ALIGNMENT targets — left↔left / right↔right — so uppers line up with
  // the run below exactly, no manual pixel nudging.
  const alignNbrs = placements.filter((q) => {
    if (q === p || q.id === p.id || (q.wallId || null) !== (p.wallId || null)) return false;
    if (!_sameFace(q)) return false;
    const qd = lib[q.type];
    return qd ? !layerCollides(def.role, qd.role) : false;
  });

  let abs = p.absLeft;
  /* SIDE PANELS (2026-09-07): magnets seat the module's REAL faces — its own
   * boards included — so a panel never lands inside its neighbour or the wall. */
  const _pspan = panelSpanOf(p, def);
  let pLeft = abs + _pspan.lo, pRight = abs + _pspan.hi;
  const _ssp = wallInnerSpan(wall, walls);

  // Left side
  let bestLeft = null, bestLeftDist = Infinity;
  const distToWallLeft = pLeft - _ssp.off0;
  if (distToWallLeft >= 0 && distToWallLeft <= snapLeftM) { bestLeft = _ssp.off0 - _pspan.lo; bestLeftDist = distToWallLeft; }
  neighbors.forEach((q) => {
    const qDef = lib[q.type]; if (!qDef) return;
    // GOLA CLEARANCE (2026-08-22): a gola side keeps a hard 30mm gap — the
    // magnet seats AT the clearance, never flush into the profile.
    const clr = pairMinGapLR(q, qDef, p, def);
    const qRight = q.absLeft + qDef.width + sidePanelExtents(q, qDef).right + clr;   // q's real right face
    const gap = pLeft - qRight;
    if (gap >= 0 && gap <= snapLeftM && gap < bestLeftDist) { bestLeft = qRight - _pspan.lo; bestLeftDist = gap; }
  });
  // GOLA CHANNEL edges (2026-08-30): a module the extended post passes
  // (wall units above a connected gola) magnets AT the channel — exactly 30mm
  const _golaChs = golaChannelsFor(p, def, placements, (x) => lib[x.type]);
  _golaChs.forEach((ch) => {
    const gapL = pLeft - ch.x1;
    if (gapL >= 0 && gapL <= snapLeftM && gapL < bestLeftDist) { bestLeft = ch.x1 - _pspan.lo; bestLeftDist = gapL; }
  });
  alignNbrs.forEach((q) => {
    const qDef = lib[q.type]; if (!qDef) return;
    const dL = Math.abs(pLeft - q.absLeft); // left edges align (stacking)
    if (dL > 1e-9 && dL <= snapLeftM && dL < bestLeftDist) { bestLeft = q.absLeft; bestLeftDist = dL; }
  });
  if (bestLeft !== null) { abs = bestLeft; pLeft = abs + _pspan.lo; pRight = abs + _pspan.hi; }

  // Right side
  let bestRight = null, bestRightDist = Infinity;
  const distToWallRight = (wallLen - _ssp.off1) - pRight;
  if (distToWallRight >= 0 && distToWallRight <= snapRightM) { bestRight = wallLen - _ssp.off1 - _pspan.hi; bestRightDist = distToWallRight; }
  neighbors.forEach((q) => {
    const qDef = lib[q.type]; if (!qDef) return;
    const clr = pairMinGapLR(p, def, q, qDef);
    const qLeft = q.absLeft - sidePanelExtents(q, qDef).left - clr;                   // q's real left face
    const gap = qLeft - pRight;
    if (gap >= 0 && gap <= snapRightM && gap < bestRightDist) { bestRight = qLeft - _pspan.hi; bestRightDist = gap; }
  });
  _golaChs.forEach((ch) => {
    const gapR = ch.x0 - pRight;
    if (gapR >= 0 && gapR <= snapRightM && gapR < bestRightDist) { bestRight = ch.x0 - _pspan.hi; bestRightDist = gapR; }
  });
  alignNbrs.forEach((q) => {
    const qDef = lib[q.type]; if (!qDef) return;
    const qRight = q.absLeft + qDef.width;
    const dR = Math.abs(qRight - pRight); // right edges align (stacking)
    if (dR > 1e-9 && dR <= snapRightM && dR < bestRightDist) { bestRight = qRight - def.width; bestRightDist = dR; }
  });
  if (bestRight !== null && bestLeft === null) { abs = bestRight; }

  // Span clamp, NOT a 0 floor — 0 is the centerline node; the legal minimum is the
  // signed off0 (negative at a convex corner), or the magnet to off0 above is undone.
  return Math.max(_ssp.off0 - _pspan.lo, Math.min(abs, wallLen - _ssp.off1 - _pspan.hi));
}
